<template>
    <form class="form-horizontal pl-2">
        <b-form-group
            :label-for="`appointment_type`"
            :label-cols="3"
            label="Type:"
        >
            <b-form-select
                id="appointment_type"
                v-model="data.appointment_type"
                v-validate="'required'"
                @change="typeChange"
                :disabled="!appointmentTypes.length"
                class="form-control"
                name="appointment_type"
                :options="appointmentTypes"
            >
            </b-form-select>
        </b-form-group>

        <b-form-group :label-for="`reason`" :label-cols="3" label="Complaint:">
            <b-form-input
                id="reason"
                v-model="data.reason"
                :class="{ 'border-danger': errors.has('reason') }"
                v-validate="'required'"
                autofocus
                placeholder="Complaint (required)"
                name="reason"
            ></b-form-input>
        </b-form-group>

        <b-form-group
            :label-for="`staff_id`"
            :label-cols="3"
            label="Assign To:"
        >
            <b-form-select
                id="staff_id"
                v-model="data.staff_id"
                :disabled="!providers.length"
                class="form-control"
            >
                <option disabled value="0">Assign To (Required)</option>
                <option
                    v-for="provider in providers"
                    :key="provider.id"
                    :value="provider.id"
                >
                    {{ provider.providerName }}
                </option>
            </b-form-select>
        </b-form-group>

        <b-form-group :label-for="`client_id`" :label-cols="3" label="Client:">
            <!-- client-profile -->
            <b-input
                v-if="data.module_referrer === 'client-profile'"
                readonly
                style="background-color: #fff;"
                :value="clientName(data.client)"
            />

            <!-- patient-profile -->
            <vue-select
                :options="patientOwners"
                v-else-if="data.module_referrer === 'patient-profile'"
                id="client_id"
                v-model="data.client"
                v-validate="''"
                placeholder="Client Name"
                name="client_id"
                :get-option-label="client => clientName(client)"
            />

            <!-- schedule -->
            <vet-select
                v-else
                id="client_id"
                v-model="data.client"
                v-validate="''"
                module="clients-include-inactive"
                placeholder="Client Name (optional)"
                name="client_id"
            />
        </b-form-group>

        <b-form-group
            :label-for="`patient_id`"
            :label-cols="3"
            label="Patient:"
        >
            <b-input
                v-if="data.module_referrer === 'patient-profile'"
                class="form-control"
                readonly
                :value="data.patient ? data.patient.name : '-'"
                style="background-color: #fff;"
            />
            <vet-select
                v-else
                :showMenu="!fromSchedule ? false : true"
                id="patient_id"
                v-model="data.patient"
                :disabled="!(data.client && data.client.id)"
                v-validate
                :client-id="data.client_id"
                :options="poptions"
                module="client-patient"
                placeholder="Patient Name (optional)"
                label="name"
                name="patient_id"
            />
        </b-form-group>

        <!-- if from schedule, show fields as usual -->
        <div v-if="fromSchedule">
            <b-form-group
                v-if="
                    $root.setting && _.includes([1, 2], $root.setting.location)
                "
                :label-for="`location`"
                :label-cols="3"
                label="Location:"
            >
                <b-form-select
                    id="location"
                    v-model="data.location"
                    class="form-control"
                >
                    <option :value="null" v-if="$root.setting.location == 2"
                        >In Clinic</option
                    >
                    <option
                        :value="null"
                        v-if="!data.client_id && $root.setting.location == 1"
                        >Select location (optional)</option
                    >
                    <option
                        v-for="location in locations"
                        :key="location.id"
                        :value="location"
                        >{{ location.name }}{{ location.address }}</option
                    >
                </b-form-select>
            </b-form-group>

            <b-form-group
                :label-for="`referrer_id`"
                :label-cols="3"
                label="Referred By:"
            >
                <vet-select
                    id="referrer_id"
                    v-model="data.referrer"
                    :disabled="!(data.client && data.client.id)"
                    @input="referrerChange"
                    v-validate
                    module="referrer"
                    placeholder="Select referral source (optional)"
                    name="referrer_id"
                />
            </b-form-group>

            <b-form-group
                :label-for="`startdate`"
                :label-cols="3"
                label="From:"
            >
                <vet-datetime
                    id="start"
                    v-model="data.start"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Appointment Date (required)',
                        time: 'Start Time (required)'
                    }"
                    container="#appointmentModal"
                    rules="required"
                    :step="_setting.slot_minutes"
                    name="start"
                    :isUpdateTimeDebounce="false"
                ></vet-datetime>
            </b-form-group>

            <b-form-group :label-for="`enddate`" :label-cols="3" label="To:">
                <vet-datetime
                    id="end"
                    v-model="data.end"
                    :min-time="minDate"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Appointment Date (required)',
                        time: 'End Time (required)'
                    }"
                    container="#appointmentModal"
                    rules="required"
                    :step="_setting.slot_minutes"
                    name="end"
                    :isUpdateTimeDebounce="false"
                />
            </b-form-group>

            <b-form-group :label-for="`repeat`" :label-cols="3" label="Repeat:">
                <b-form-select
                    id="repeat"
                    v-model="data.repeat"
                    :value="1"
                    v-validate="'required'"
                    name="repeat"
                    class="form-control"
                    aria-invalid="false"
                >
                    <option value="1">Does not repeat</option>
                    <option value="2">Every day (Monday - Sunday)</option>
                    <option value="3">Every weekday (Monday - Friday)</option>
                    <option value="4">Every weekend (Saturday - Sunday)</option>
                    <option value="5">Every Monday, Wednesday, Friday</option>
                    <option value="6">Every Tuesday and Thursday</option>
                    <option value="7">Every Week</option>
                    <option value="10">Every 2 Weeks</option>
                    <option value="11">Every 3 Weeks</option>
                    <option value="12">Every 4 Weeks</option>
                    <option value="8">Every Month</option>
                    <option value="9">Every Year</option>
                    <option value="13">Custom</option>
                </b-form-select>
            </b-form-group>

            <b-form-group
                :label-for="`occurence`"
                :label-cols="3"
                v-if="data.repeat > 1 && data.repeat != 13"
                label="Occurrence:"
            >
                <b-form-input
                    id="stop"
                    :class="{ 'border-danger': errors.has('stop') }"
                    v-model="data.stop"
                    v-validate="{
                        required: data.repeat > 1,
                        numeric: data.repeat > 1
                    }"
                    type="number"
                    min="0"
                    step="1"
                    class="form-control"
                    name="stop"
                    placeholder="Occurence (required)"
                />
            </b-form-group>

            <b-form-group
                v-if="data.repeat == 13"
                :label-for="`repeat`"
                :label-cols="3"
            >
                <div class="form-inline">
                    <div class="col-6 px-0" style="margin-right: -24px;">
                        <b-form-input
                            id="occurrence"
                            v-model="data.occurrence"
                            :class="{
                                'border-danger': errors.has('occurrence')
                            }"
                            type="number"
                            min="1"
                            max="99"
                            step="1"
                            name="occurrence"
                            style="width: 3.5rem;"
                        />
                        <span class="ml-2 mr-2">times every</span>
                    </div>
                    <div class="col-2 px-0 mr-1">
                        <b-form-input
                            id="specific_interval"
                            v-model.number="data.specific_interval"
                            :value="1"
                            v-validate="'required|numeric'"
                            type="number"
                            min="1"
                            max="99"
                            step="1"
                            :class="{
                                'border-danger': errors.has('specific_interval')
                            }"
                            name="specific_interval"
                            style="width: 3.5rem;"
                        ></b-form-input>
                    </div>
                    <div>
                        <b-form-select
                            id="repeat_interval"
                            v-model="data.repeat_interval"
                            :options="repeatIntervalOptions"
                            :value="'days'"
                            name="repeat_interval"
                            class="form-control custom-select"
                            style="width: 8.1rem;"
                        ></b-form-select>
                    </div>
                </div>
            </b-form-group>

            <b-form-group
                :label-for="`save_event_type`"
                :label-cols="3"
                v-if="data.id != null && data.original_repeat > 1"
                label="Apply To:"
            >
                <b-form-select
                    id="save_event_type"
                    v-model="data.save_event_type"
                    :value="1"
                    name="save_event_type"
                    class="form-control"
                    aria-invalid="false"
                >
                    <option value="1">Only this event</option>
                    <option value="2">This and following events</option>
                    <option value="3">All events</option>s
                </b-form-select>
            </b-form-group>
            <b-form-group
                :label-for="`notes`"
                :label-cols="3"
                label="Notes:"
                class="mb-0"
            >
                <editable
                    id="notes"
                    v-model="data.note"
                    placeholder="Notes (optional)"
                />
            </b-form-group>
        </div>

        <!-- if from patient or client module -->
        <div
            v-if="
                data.module_referrer === 'patient-profile' ||
                data.module_referrer === 'client-profile'
            "
        >
            <b-form-group
                :label-for="`referrer_id`"
                :label-cols="3"
                label="Date/Time:"
            >
                <b-select
                    v-model="slotType"
                    @change="getSlot"
                    class="form-control"
                >
                    <option :value="1">Next available</option>
                    <option :value="2">Select date</option>
                </b-select>
            </b-form-group>

            <!-- Next available (slot type 1) -->
            <b-form-group
                v-if="slotType === 1"
                :label-for="`startdate`"
                :label-cols="3"
                label=""
            >
                <b-input
                    :value="isLoadingSlot ? 'Loading...' : nextAvailableSlotStr"
                    readonly
                    style="background-color: #fff;"
                />
            </b-form-group>

            <!-- Select date (slot type 2) -->
            <b-form-group
                v-if="slotType === 2"
                :label-for="`startdate`"
                :label-cols="3"
                label=""
            >
                <vet-datetime
                    id="start"
                    v-model="data.start"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Appointment Date (required)',
                        time: 'Start Time (required)'
                    }"
                    container="#appointmentModal"
                    rules="required"
                    :step="_setting.slot_minutes"
                    name="start"
                    format="datetime"
                    :timeslot="timeslot"
                    :isLoadingTimeslot="isLoadingSlot"
                    @timeslot-change="changeTimeslot"
                    :isUpdateTimeDebounce="false"
                ></vet-datetime>
            </b-form-group>
        </div>
    </form>
</template>
<script>
// -- Use 'module' to refer root path of src (check webpack config {resolve->alias})
import vetSelect from "@/module/commons/vet-select.vue";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import { debounce, find, get, sortBy } from "lodash";
import { DateTime } from "luxon";
import vueSelect from "vue-select";
import Editable from "@/components/elements/editable-textarea";

export default {
    components: {
        vetSelect,
        vetDatetime,
        vueSelect,
        Editable
    },
    // eslint-disable-next-line vue/require-prop-types
    props: ["data"],
    data() {
        return {
            resources: {},
            appointmentTypes: [],
            providers: [],
            poptions: [],
            locations: [],
            referrer: null,
            referrers: [],
            slotType: this.data.timeSlotType || 1,
            isMounted: false,
            isLoadingSlot: true,
            nextAvailableSlot: null, // null | Datetime
            timeslot: [],
            repeatIntervalOptions: [
                { value: 1, text: "Day(s)" },
                { value: 2, text: "Week(s)" },
                { value: 3, text: "Month(s)" },
                { value: 4, text: "Year(s)" }
            ]
        };
    },
    computed: {
        patientOwners() {
            let owners = this.data.patient
                ? this.data.patient.active_owners
                : [];

            return owners;
        },
        fromSchedule() {
            if (this.data.module_referrer === "patient-profile") {
                return false;
            }
            if (this.data.module_referrer === "client-profile") {
                return false;
            }
            return true;
        },
        minDate() {
            if (this.data.start) {
                return this.data.start.startOf("day");
            }
            return null;
        },
        nextAvailableSlotStr() {
            if (this.nextAvailableSlot) {
                return this.nextAvailableSlot
                    .setZone(this._timeZone)
                    .toFormat("cccc, " + this._dateFormat + " h:mm a");
            }
            return "Not available";
        }
    },
    watch: {
        "data.client": {
            handler(newData, oldData) {
                if (this.data.module_referrer === "patient-profile") {
                    this.data.client_changed = 1;
                    this.data.client_id = newData.id;
                    return;
                }

                let self = this;
                self.data.client_id = newData ? newData.id : null;
                self.data.type = newData ? 0 : 1;
                self.poptions = [];

                if (newData) {
                    this.getLocations(newData.id).then(locations => {
                        if (self.$root.setting.location == 1) {
                            self.data.location = find(locations, {
                                id: newData.id
                            });
                        } else {
                            self.data.location = null;
                        }
                    });
                    this.getReferrer(newData.id).then(referrer => {
                        this.data.referrer = referrer[0];
                        this.data.referral_id = this._bindReferrer(
                            this.data.referrer
                        );
                    });

                    // -- change to other client
                    if (oldData != newData) {
                        if (!self.data.patient && self.data.note) {
                            // self.data.note += "\n";
                        } else {
                            self.data.note = "";
                        }

                        this.data.client_changed = 1;
                        self.data.patient = null;
                        self.data.location = null;
                    } else {
                        this.data.client_changed = 0;
                    }
                } else {
                    this.data.patient_id = null;
                    this.invoices = [];
                    this.locations = [];
                    this.poptions = [];
                    this.data.patient_id = null;
                    this.data.patient = null;
                    self.data.location = null;
                    this.data.referral_id = null;
                    this.data.referrer = null;
                }
            }
        },
        "data.patient": {
            handler(newData, oldData) {
                if (newData) {
                    this.data.patient_id = newData.id;
                    // -- remove referrer when patient-actor inactive
                    if (
                        newData.patient_actors &&
                        this.data.referrer &&
                        find(newData.patient_actors, {
                            actor_id: this.data.referrer.id,
                            isactive: false,
                            relation_type: 8
                        })
                    ) {
                        this.data.referral_id = null;
                        this.data.referrer = null;
                    }
                } else {
                    this.data.patient_id = null;
                }

                if (oldData !== newData) {
                    this.data.patient_changed = 1;
                }
            }
        },
        "data.start"(newDt, oldDt) {
            this.typeChange();
            this.$emit("resetManualCheckEndLTEStart"); // VET-10601 - Cannot replicate the issue 1 and 2, so we reset manualCheckEndLTEStart and later after click save/next will check again the end and start date and then disable the button // Assume this code will be applied if the issue 1 and 2 occurred again
            this.data.time_changed = true;

            if (
                !this.fromSchedule &&
                this.slotType === 2 &&
                newDt.toFormat("ddMMyyyy") !== oldDt.toFormat("ddMMyyyy")
            ) {
                this.$emit("update-slot", { hasTimeSlot: false });
                this.getSlot(true);
            }
        },
        "data.end"(newData) {
            this.data.time_changed = true;
            this.$emit("resetManualCheckEndLTEStart");
            this.updateDraft({ end: newData });
        },
        "data.appointment_type"() {
            if (this.isMounted) {
                this.getSlot(true);
            }
        },
        "data.staff_id"() {
            if (this.isMounted) {
                this.data.staff = find(this.providers, {
                    id: this.data.staff_id
                });
                this.data.is_staff = this.data.staff.type;

                this.getSlot(true);
                this.updateDraft({ resourceId: this.data.staff_id });
            }
        }
    },
    created() {},
    mounted() {
        let self = this;
        self.$emit("beforeload");
        let process = [self.getProviders(), self.getAppointmentType()];
        if (self.data.client) {
            process.push(self.getLocations(self.data.client.id));
        }

        self.$emit("loading");
        Promise.all(process).then(() => {
            self.$emit("loaded");
            /**
             * only trigger typeChange when mounted if data.end is empty
             * this is intefering with custom end time selected by client
             * it changes the end time field based on appointment type each
             * time modal is opened when editing appointment
             **/
            if (!this.data.end) {
                self.typeChange();
            }
            if (!this.data.id) {
                this.$root.$emit("createDraft", this.data);
            }

            if (!this.data.time_changed) {
                this.data.time_changed = false;
            }
            self.$validator.reset();

            if (!this.isMounted) {
                this.isMounted = true;
                this.getSlot(this.data.hasTimeSlot ? false : true);
            }
        });
    },
    methods: {
        clientName(client) {
            if (!client) {
                return "";
            }

            let firstname = client.firstname || client.firstName;
            let lastname = client.lastname || client.lastName;
            let str = `${firstname} ${lastname}`;
            let status = client.statusClient || client.status;

            if (status && status.id !== 1) {
                str += ` (${status.name})`;
            }

            return str;
        },
        changeTimeslot(time) {
            let start = this.data.start;
            let newValue = `${start.toFormat("ddMMyyyy")} ${time}`;
            let startDt = DateTime.fromFormat(newValue, "ddMMyyyy HHmm", {
                zone: this._timeZone
            });

            this.$emit("update-slot", {
                start: startDt
            });
        },
        /**
         * what is "reset" here?
         * reset = true: refetch time slot, set default to first available option
         * reset = false: refetch time slot, but still maintain the selected value from previous selection
         * real use case:
         * 1. user choose available date and time (slotType = 2)
         * 2. user choose their preferred date and time
         * 3. user click "Next" (wizard)
         * 4. user forgot something, they click the "Back" button (wizard)
         * 5. `reset` flag will be used to determine if we have to reset the time or not
         */
        getSlot(reset = false) {
            if (this.fromSchedule) {
                return;
            }
            if (this.slotType === 1) {
                this.getNextAvailableSlot();
                this.$emit("update-slot", { timeSlotType: 1 });
            } else if (this.slotType === 2) {
                let useSavedValue = reset ? false : true;
                this.getTimeSlot(useSavedValue);
                this.$emit("update-slot", { timeSlotType: 2 });
            }
        },
        async getTimeSlot(useSavedValue = false) {
            const clearTimeSlot = () => {
                this.timeslot = [];
                this.$emit("update-slot", { hasTimeSlot: false });
            };

            let day = this.data.start;

            this.isLoadingSlot = true;

            let result = await this.getAppointmentSlot({
                typeId: this.data.appointment_type,
                providerId: this.data.staff_id,
                start: day.startOf("day").toFormat("yyyy-MM-dd"),
                end: day.plus({ day: 1 }).endOf("day").toFormat("yyyy-MM-dd"), // plus 1 - is a must
                tz: day.zone.zoneName
            });

            this.isLoadingSlot = false;

            if (!result) {
                clearTimeSlot();
                return;
            }

            let key = Object.keys(result)[0];
            let slots = result[key];

            if (!slots.length === 0) {
                clearTimeSlot();
                return;
            }

            let list = [];
            for (let slot of slots) {
                let dt = DateTime.fromSeconds(slot.start).setZone(
                    this._timeZone
                );

                list.push({
                    text: dt.toFormat("hh:mm a"),
                    value: dt.toFormat("HHmm") // 24 hours format
                });
            }

            this.timeslot = list;

            // use saved value (if any).
            // use case: user click back button in modal wizard
            if (useSavedValue) {
                this.isLoadingSlot = false;
                this.$emit("update-slot", { hasTimeSlot: true });
            }
            // reset to default first option
            else if (list[0]) {
                this.changeTimeslot(list[0].value);
                this.$emit("update-slot", { hasTimeSlot: true });
            }
            // set to empty, thus disable 'Next' button
            else {
                clearTimeSlot();
            }
        },
        async getNextAvailableSlot() {
            let start = DateTime.local().setZone(this._timeZone);
            let end = start.plus({ day: 20 }); // check for next 20 days including weekends

            this.isLoadingSlot = true;

            let result = await this.getAppointmentSlot({
                typeId: this.data.appointment_type,
                providerId: this.data.staff_id,
                start: start.toFormat("yyyy-MM-dd"),
                end: end.toFormat("yyyy-MM-dd"),
                tz: start.zone.zoneName
            });

            if (!result) {
                this.isLoadingSlot = false;
                this.nextAvailableSlot = null;
                this.$emit("update-slot", { hasTimeSlot: false });
                return;
            }

            let resultDays = Object.keys(result);
            let selected;

            for (const key of resultDays) {
                if (result[key].length > 0) {
                    selected = result[key][0];
                    break;
                }
            }

            if (!selected) {
                this.isLoadingSlot = false;
                this.nextAvailableSlot = null;
                this.$emit("update-slot", { hasTimeSlot: false });
                return;
            }

            this.isLoadingSlot = false;

            let finalResult = DateTime.fromSeconds(selected.start);

            this.nextAvailableSlot = finalResult;

            this.$emit("update-slot", {
                start: finalResult,
                hasTimeSlot: true
            });
        },
        async getAppointmentSlot({ typeId, providerId, start, end, tz }) {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}schedule/online-booking/timetable`,
                {
                    params: {
                        type_id: typeId,
                        staff_id: providerId, // online booking uses "provider_id"
                        start: start,
                        end: end,
                        tz // this is for reference only, wont involve in any processing
                    }
                }
            );

            let result = [];

            if (res.data && res.data.response && res.data.response.resources) {
                result = res.data.response.resources;
            }

            if (result.length === 0) {
                return null;
            }

            return result;
        },
        getAppointmentType() {
            let self = this;

            return this.$http
                .get(process.env.BARRACUDA + "account/appointment-type")
                .then(function (response) {
                    self.appointmentTypes = sortBy(
                        response.data.response?.resource?.data?.map(type => ({
                            ...type,
                            value: type.id,
                            text:
                                type.type + " (" + type.duration + " minutes)",
                            name: type.type,
                            index: type.index == null ? 0 : type.index
                        })),
                        "index"
                    );
                    if (!self.data.appointment_type) {
                        self.data.appointment_type =
                            self.appointmentTypes[0].value;
                    }
                });
        },
        getProviders() {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "account/schedule-columns?is_settings=0"
                )
                .then(function (response) {
                    self.providers = get(
                        response,
                        "data.response.resource.data",
                        []
                    );

                    self.providers = _.map(self.providers, element => ({
                        ...element,
                        id: element.type == 1 ? element.staff.id : element.id,
                        providerName:
                            element.display_name == "" ||
                            element.display_name == null
                                ? element.staff.firstname +
                                  " " +
                                  element.staff.lastname
                                : element.display_name
                    }));

                    self.data.staff = _.find(self.providers, {
                        id: self.data.staff_id
                    });

                    if (self.data.staff) {
                        self.data.is_staff = self.data.staff.type;
                    } else if (
                        self.data.module_referrer === "patient-profile" ||
                        self.data.module_referrer === "client-profile"
                    ) {
                        let defaultProvider = self.providers[0];

                        if (defaultProvider) {
                            self.data.is_staff = defaultProvider.type;
                            self.data.staff = defaultProvider;
                            self.data.staff_id = defaultProvider.id;
                        } else {
                            self.data.is_staff = 0;
                        }
                    }
                });
        },
        getLocations(client_id) {
            let self = this;
            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "actor/client/" +
                        client_id +
                        "/location"
                )
                .then(function (response) {
                    self.locations = response.data.response.resources;
                    if (self.data.appointmentLocation) {
                        self.data.location = find(self.locations, {
                            id: self.data.appointmentLocation.location_id
                        });
                    }
                    return self.locations;
                });
        },
        getReferrer() {
            let self = this;
            return self.$http
                .get(
                    process.env.ACTOR_SERVICE +
                        "client/" +
                        self.data.client_id +
                        "/referrer"
                )
                .then(function (response) {
                    return response.data.response.resource;
                });
        },
        referrerChange(referrer) {
            if (referrer) {
                this.data.referrer = referrer;
                this.data.referral_id = this._bindReferrer(referrer);
            } else {
                this.data.referrer = null;
                this.data.referral_id = null;
            }
        },
        typeChange() {
            let appointmentType = find(this.appointmentTypes, {
                value: this.data.appointment_type
            });

            if (appointmentType) {
                this.data.appointment_type_name = appointmentType.name;
                this.data.appointmentType = appointmentType;
            }

            if (appointmentType && this.data.start) {
                this.data.end = this.data.start.plus({
                    minutes: appointmentType.duration
                });
            }
            return this.data.end;
        },
        updateDraft: debounce(function () {
            if (
                !this.data.id &&
                !this.getDateFormat.id &&
                this.data.start &&
                this.data.end
            ) {
                this.$socket.client.emit("v-broadcast-all", {
                    businessId: this.$root.setting.business_id,
                    action: "update",
                    start: this.data.start.toISO(),
                    end: this.data.end.toISO(),
                    resourceId: this.data.staff_id
                });
            }
        }, 500)
    }
};
</script>
